import { useCallback, useRef } from 'react';
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';

/** Start the next page slightly before the last row is reached. */
const NEAR_END_THRESHOLD_PX = 240;
/** Floor between two triggers so a scroll burst can't queue several pages. */
const TRIGGER_COOLDOWN_MS = 400;

export interface UseScrollPaginationOptions {
  /** Ask for the next page — must be a no-op when nothing is left to load. */
  onLoadMore: () => void;
  /** False while the first page loads, while a page is in flight, or once `hasNext` is false. */
  enabled: boolean;
}

export interface ScrollPaginationProps {
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onContentSizeChange: (width: number, height: number) => void;
  onLayout: (event: LayoutChangeEvent) => void;
  scrollEventThrottle: number;
}

/**
 * Spreadable ScrollView props that request the next page when the user scrolls
 * near the end. Used by ADHA web listing and ADHA mobile home.
 * Also covers a page too short to scroll (small `limit`) so the list can grow.
 *
 * Scroll events arrive far faster than React can re-render, so triggers are also
 * rate limited here; `onLoadMore` itself must stay safe to call repeatedly.
 */
export function useScrollPagination({
  onLoadMore,
  enabled,
}: UseScrollPaginationOptions): ScrollPaginationProps {
  const viewportHeightRef = useRef(0);
  const lastTriggerAtRef = useRef(0);

  const requestMore = useCallback(() => {
    const now = Date.now();
    if (now - lastTriggerAtRef.current < TRIGGER_COOLDOWN_MS) {
      return;
    }
    lastTriggerAtRef.current = now;
    onLoadMore();
  }, [onLoadMore]);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!enabled) {
        return;
      }
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const distanceToEnd =
        contentSize.height - (contentOffset.y + layoutMeasurement.height);
      if (distanceToEnd <= NEAR_END_THRESHOLD_PX) {
        requestMore();
      }
    },
    [enabled, requestMore],
  );

  const handleContentSizeChange = useCallback(
    (_width: number, height: number) => {
      if (!enabled) {
        return;
      }
      // Nothing to scroll yet — grow the grid until it overflows the viewport.
      const viewportHeight = viewportHeightRef.current;
      if (viewportHeight > 0 && height <= viewportHeight) {
        requestMore();
      }
    },
    [enabled, requestMore],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    viewportHeightRef.current = event.nativeEvent.layout.height;
  }, []);

  return {
    onScroll: handleScroll,
    onContentSizeChange: handleContentSizeChange,
    onLayout: handleLayout,
    scrollEventThrottle: 16,
  };
}
